import React, { useState, useRef, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faHouse, faTree, faMapLocationDot } from '@fortawesome/free-solid-svg-icons';
import { ZOOM_PRESETS, ZoomPreset } from './presets';
import './QuickZoomControl.scss';

interface QuickZoomControlProps {
  map: mapboxgl.Map;
}

const QuickZoomControl: React.FC<QuickZoomControlProps> = ({ map }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handlePresetSelect = (preset: ZoomPreset) => {
    map.fitBounds(preset.bounds, { padding: 50 });
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getPresetIcon = (preset: ZoomPreset) => {
    switch (preset.icon) {
      case 'home':
        return faHouse;
      case 'park':
        return faTree;
      default:
        return faMapLocationDot;
    }
  };

  return (
    <div className="quick-zoom-control map-control" ref={dropdownRef}>
      <button
        className="quick-zoom-button control-button"
        title="Quick zoom to preset location"
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <FontAwesomeIcon icon={faMagnifyingGlass} />
      </button>

      {isOpen && (
        <div className="quick-zoom-dropdown" role="listbox">
          {ZOOM_PRESETS.map((preset) => (
            <button
              key={preset.id}
              className={`quick-zoom-item ${preset.icon === 'home' ? 'home-item' : ''}`}
              onClick={() => handlePresetSelect(preset)}
              role="option"
            >
              <FontAwesomeIcon icon={getPresetIcon(preset)} className="preset-icon" />
              <span>{preset.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuickZoomControl;
